/**
 * Clients and their sites.
 *
 * The level that was missing. A flat list of site names has no context — "Head
 * Office" belonging to whom — and the contact details you need when a camera
 * goes down at 2am had nowhere to live but a spreadsheet.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';

import {
  ApiError,
  createClient,
  createSite,
  deleteClient,
  deleteSite,
  fetchClients,
  fetchSites,
  type ApiClient,
  type ApiSite,
} from './api.ts';

function addressOf(client: ApiClient): string {
  return [client.address_line1, client.address_line2, client.city, client.region, client.postal_code, client.country]
    .filter((part) => part && part.trim() !== '')
    .join(', ');
}

export function Clients({ onChanged }: { onChanged: () => void }): React.JSX.Element {
  const [clients, setClients] = useState<ApiClient[]>([]);
  const [sites, setSites] = useState<ApiSite[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: '', contactName: '', contactEmail: '', contactPhone: '',
    addressLine1: '', addressLine2: '', city: '', region: '', postalCode: '', country: '', notes: '',
  });
  const [siteName, setSiteName] = useState('');

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([fetchClients(), fetchSites()]);
      setClients(c);
      setSites(s);
    } catch {
      setMessage('Could not load clients.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  async function submitClient(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (form.name.trim() === '') {
      setMessage('A client name is required.');
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await createClient({ ...form, name: form.name.trim() });
      setForm({
        name: '', contactName: '', contactEmail: '', contactPhone: '',
        addressLine1: '', addressLine2: '', city: '', region: '', postalCode: '', country: '', notes: '',
      });
      setAdding(false);
      await load();
      onChanged();
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : 'Could not create the client.');
    } finally {
      setBusy(false);
    }
  }

  async function addSite(clientId: string, event: FormEvent): Promise<void> {
    event.preventDefault();
    if (siteName.trim() === '') return;
    setBusy(true);
    setMessage(null);
    try {
      await createSite({ clientId, name: siteName.trim() });
      setSiteName('');
      await load();
      onChanged();
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : 'Could not create the site.');
    } finally {
      setBusy(false);
    }
  }

  async function removeSite(site: ApiSite): Promise<void> {
    if (!window.confirm(`Delete site "${site.name}"?`)) return;
    setMessage(null);
    try {
      await deleteSite(site.id);
      await load();
      onChanged();
    } catch (error) {
      // The API refuses while devices remain and says how many, which is more
      // useful than a generic failure.
      setMessage(error instanceof ApiError ? error.message : 'Could not delete the site.');
    }
  }

  async function removeClient(client: ApiClient): Promise<void> {
    if (!window.confirm(`Delete client "${client.name}"?`)) return;
    setMessage(null);
    try {
      await deleteClient(client.id);
      await load();
      onChanged();
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : 'Could not delete the client.');
    }
  }

  return (
    <section className="section">
      <div className="section__head">
        <button type="button" className="btn btn--primary" onClick={() => setAdding((v) => !v)}>
          {adding ? 'Cancel' : 'Add client'}
        </button>
      </div>

      {message ? (
        <p className="notice notice--error" role="alert" style={{ marginBottom: 'var(--s4)' }}>
          {message}
        </p>
      ) : null}

      {adding ? (
        <div className="card" style={{ marginBottom: 'var(--s4)' }}>
          <div className="card__body">
            <form className="form" onSubmit={(e) => void submitClient(e)}>
              <div className="field">
                <label htmlFor="c-name">Client name</label>
                <input id="c-name" value={form.name} onChange={set('name')} placeholder="Riverside Logistics" />
              </div>

              <div className="form__row">
                <div className="field">
                  <label htmlFor="c-contact">Contact name</label>
                  <input id="c-contact" value={form.contactName} onChange={set('contactName')} />
                </div>
                <div className="field">
                  <label htmlFor="c-phone">Phone</label>
                  <input id="c-phone" value={form.contactPhone} onChange={set('contactPhone')} />
                </div>
              </div>

              <div className="field">
                <label htmlFor="c-email">Email</label>
                <input id="c-email" type="email" value={form.contactEmail} onChange={set('contactEmail')} />
              </div>

              <div className="field">
                <label htmlFor="c-addr1">Address</label>
                <input id="c-addr1" value={form.addressLine1} onChange={set('addressLine1')} placeholder="12 Wharf Road" />
                <input value={form.addressLine2} onChange={set('addressLine2')} placeholder="Line 2 (optional)" />
              </div>

              <div className="form__row">
                <div className="field">
                  <label htmlFor="c-city">City</label>
                  <input id="c-city" value={form.city} onChange={set('city')} />
                </div>
                <div className="field">
                  <label htmlFor="c-region">State / region</label>
                  <input id="c-region" value={form.region} onChange={set('region')} />
                </div>
              </div>

              <div className="form__row">
                <div className="field">
                  <label htmlFor="c-post">Postal code</label>
                  <input id="c-post" value={form.postalCode} onChange={set('postalCode')} />
                </div>
                <div className="field">
                  <label htmlFor="c-country">Country</label>
                  <input id="c-country" value={form.country} onChange={set('country')} />
                </div>
              </div>

              <div className="field">
                <label htmlFor="c-notes">Notes</label>
                <input id="c-notes" value={form.notes} onChange={set('notes')} placeholder="Gate code, key holder, access hours" />
                <span className="field__hint">Only the name is required. The rest can be filled in later.</span>
              </div>

              <div className="form__actions">
                <button type="submit" className="btn btn--primary" disabled={busy}>
                  {busy ? 'Saving…' : 'Create client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {clients.length === 0 ? (
        <div className="empty">
          <p className="empty__title">No clients yet</p>
          <p>Add the business you are installing for, then create sites under it.</p>
        </div>
      ) : (
        <div className="grid">
          {clients.map((client) => {
            const clientSites = sites.filter((s) => s.client_id === client.id);
            const open = expanded === client.id;
            const address = addressOf(client);

            return (
              <div key={client.id} className="card">
                <div className="card__body">
                  <div className="device__top">
                    <div>
                      <h3 className="device__name">{client.name}</h3>
                      <p className="device__site">
                        {client.site_count} site{client.site_count === 1 ? '' : 's'} ·{' '}
                        {client.device_count} device{client.device_count === 1 ? '' : 's'}
                      </p>
                    </div>
                  </div>

                  {client.contact_name || client.contact_phone || client.contact_email ? (
                    <p className="device__seen" style={{ marginTop: 'var(--s3)' }}>
                      {[client.contact_name, client.contact_phone, client.contact_email]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  ) : null}
                  {address ? <p className="device__seen">{address}</p> : null}
                  {client.notes ? <p className="stale">{client.notes}</p> : null}

                  {open ? (
                    <>
                      <ul className="cameras">
                        {clientSites.map((site) => (
                          <li key={site.id} className="camera">
                            <span className="camera__name">
                              {site.name}
                              <span className="camera__state">
                                {' '}
                                · {site.device_count} device{site.device_count === 1 ? '' : 's'}
                              </span>
                            </span>
                            <button type="button" className="link" onClick={() => void removeSite(site)}>
                              Delete
                            </button>
                          </li>
                        ))}
                        {clientSites.length === 0 ? (
                          <li className="camera">
                            <span className="camera__state">No sites yet</span>
                          </li>
                        ) : null}
                      </ul>

                      <form className="form" style={{ marginTop: 'var(--s3)' }} onSubmit={(e) => void addSite(client.id, e)}>
                        <div className="field">
                          <label htmlFor={`site-${client.id}`}>New site</label>
                          <input
                            id={`site-${client.id}`}
                            value={siteName}
                            onChange={(e) => setSiteName(e.target.value)}
                            placeholder="Head Office"
                          />
                        </div>
                        <div className="form__actions">
                          <button type="submit" className="btn btn--sm btn--primary" disabled={busy}>
                            Add site
                          </button>
                        </div>
                      </form>
                    </>
                  ) : null}
                </div>

                <div className="card__foot">
                  <button type="button" className="btn btn--sm" onClick={() => setExpanded(open ? null : client.id)}>
                    {open ? 'Hide sites' : 'Sites'}
                  </button>
                  <button type="button" className="btn btn--sm btn--danger" onClick={() => void removeClient(client)}>
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
